/**
 * セルフサインアップ（R0 §2.1 / §2.2）。
 *
 * API 側（`POST /api/v1/public/signup`・`/signup/verify`・`/signup/resend`）は
 * 実装済みだったが、**叩く画面が存在しなかった**ため、申し込む手段が
 * 事実上「運営に問い合わせる」しか無かった。ここがその画面。
 *
 * 守っていること:
 *   1. **同意はチェックボックスの表示だけで済ませない。** 規約と
 *      プライバシーポリシーの**現行版**（`GET /public/legal`）を読み、
 *      その版番号を送る。サーバが版の一致を検証し、文面ハッシュを自分で
 *      計算して `consents` に残す。
 *   2. **申し込みできない状態を隠さない。** 事業者情報が未設定なら
 *      `GET /public/config` の `features.signup` が false になり、
 *      その旨を出して入力欄を出さない（送信して 503 を食う体験にしない）。
 *   3. **メール確認が済むまで契約は成立しない**ことを画面にも書く。
 *      サーバも `pending` のまま止める（未確認アドレスで active にしない）。
 *   4. **総額（税込）で表示する。** `GET /public/plans` が
 *      `monthlyPriceInclTax` と `taxRateBp` を返すので、それをそのまま出す。
 *      画面で税額を計算しない（計算が散らばると LP・特商法表記とずれる）。
 *      以前はここだけ税抜（`monthlyPrice`）を「月額 N 円」と出していて、
 *      LP（税込）・特商法ページ（「総額を表示します」）と食い違っていた。
 *   5. **送信前に最終確認画面を挟む**（特商法 12 条の 6・特定申込みを受ける際の表示）。
 *      分量・販売価格（税込）・支払時期と方法・引渡時期・撤回/解除・
 *      無料試用の日数と課金開始日を、送信ボタンと同じ画面で一覧できるようにする。
 *      文言は法定ページと同じ値（`/public/plans` の `trialDays` など）から出す。
 */
import * as React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  Input,
  formatBytes,
  formatCount,
} from '@mail-sv/ui';
import { Mail, CheckCircle2 } from 'lucide-react';
import { SIGNUP_CONSENT_KINDS, type SignupConsentKind } from '@mail-sv/shared';
import { publicApi, ApiError, errorMessage } from '../lib/api.js';
import { FormAlert, SubmitBar, type FieldErrors, hasErrors } from '../components/form.js';
import { useAppNavigate, useSearchParams } from '../lib/nav.js';
import { env } from '../env.js';

/* ------------------------------------------------------------------ 型 */

interface PublicConfig {
  features: { signup: boolean };
  legal: { draft: boolean; documents: { slug: string; title: string; version: string; published: boolean }[] };
}

interface PublicPlan {
  code: string;
  name: string;
  description: string | null;
  /** 税抜。**表示には使わない**（総額表示は `monthlyPriceInclTax`）。 */
  monthlyPrice: number;
  monthlyPriceInclTax: number;
  currency: string;
  limits: {
    domainsMax: number;
    mailboxesMax: number;
    storageBytesTotal: number;
    sendPerDayMax: number;
  };
}

/** `GET /public/plans` の応答。**税率と試用日数はサーバが正**。 */
interface PublicPlansResponse {
  items: PublicPlan[];
  taxRateBp: number;
  trialDays: number;
}

interface SignupResponse {
  organizationId: string;
  verificationRequired: boolean;
  message: string;
}

type FormKey = 'organizationName' | 'slug' | 'contactName' | 'email' | 'password' | 'domain' | 'consent';

/* ------------------------------------------------------------------ 画面 */

export function SignupPage() {
  const configQuery = useQuery({
    queryKey: ['public', 'config'],
    queryFn: () => publicApi.get<PublicConfig>('/config'),
    staleTime: 60 * 1000,
  });
  const plansQuery = useQuery({
    queryKey: ['public', 'plans'],
    queryFn: () => publicApi.get<PublicPlansResponse>('/plans'),
    staleTime: 60 * 1000,
  });

  const [organizationName, setOrganizationName] = React.useState('');
  const [slug, setSlug] = React.useState('');
  const [contactName, setContactName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [domain, setDomain] = React.useState('');
  const [planCode, setPlanCode] = React.useState('');
  const [consented, setConsented] = React.useState(false);
  const [errors, setErrors] = React.useState<FieldErrors<FormKey>>({});
  const [done, setDone] = React.useState<SignupResponse | null>(null);
  /**
   * 入力欄（`form`）と最終確認（`confirm`）の 2 段。
   *
   * **確認画面を別ルートにしない。** 別 URL にすると、入力値を保つために
   * 一度サーバへ送るか、ブラウザに置くかのどちらかが要る。特商法 12 条の 6 が
   * 求めるのは「申込みの内容を**送信する直前に**一覧で確認できること」なので、
   * 同じ画面で切り替えるのがいちばん素直で、途中離脱の穴も作らない。
   */
  const [step, setStep] = React.useState<'form' | 'confirm'>('form');

  // `?? []` を毎回作ると useEffect の依存が毎描画で変わるので memo で固定する。
  const plans = React.useMemo(() => plansQuery.data?.items ?? [], [plansQuery.data]);
  const taxRateBp = plansQuery.data?.taxRateBp ?? 1000;
  const trialDays = plansQuery.data?.trialDays ?? 0;
  const selectedPlan = plans.find((p) => p.code === planCode) ?? null;

  /**
   * LP の料金表から `?plan=standard` で来たときに、そのプランを選んだ状態で開く。
   *
   * **URL の値をそのまま使わない。** サーバが返した公開プランに実在する code の
   * ときだけ採用し、無ければ既定（先頭）に落とす。存在しない code を
   * `planCode` に入れると、選択が空のまま送信されて 400 になり、
   * 利用者からは「申し込めない」だけに見える。
   */
  React.useEffect(() => {
    if (planCode || plans.length === 0) return;
    const requested = new URLSearchParams(window.location.search).get('plan');
    const matched = requested ? plans.find((p) => p.code === requested) : undefined;
    setPlanCode(matched?.code ?? plans[0]!.code);
  }, [planCode, plans]);

  /** 同意に使う「現行版」。**画面が勝手に版番号を作らない**（必ずサーバの値）。 */
  const consentDocuments = React.useMemo(() => {
    const docs = configQuery.data?.legal.documents ?? [];
    return SIGNUP_CONSENT_KINDS.map((kind) => docs.find((d) => d.slug === kind)).filter(
      (d): d is NonNullable<typeof d> => Boolean(d),
    );
  }, [configQuery.data]);

  const mutation = useMutation({
    mutationFn: (): Promise<SignupResponse> =>
      publicApi.post<SignupResponse>('/signup', {
        organizationName: organizationName.trim(),
        slug: slug.trim().toLowerCase(),
        contactName: contactName.trim(),
        email: email.trim(),
        password,
        planCode,
        ...(domain.trim() ? { domain: domain.trim().toLowerCase() } : {}),
        acceptedDocuments: consentDocuments.map((d) => ({
          kind: d.slug as SignupConsentKind,
          version: d.version,
        })),
      }),
    onSuccess: (res) => setDone(res),
  });

  function validate(): FieldErrors<FormKey> {
    const next: FieldErrors<FormKey> = {};
    if (!organizationName.trim()) next.organizationName = '入力してください';
    if (!/^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/.test(slug.trim().toLowerCase())) {
      next.slug = '3〜32 文字の英小文字・数字・ハイフンで指定してください';
    }
    if (!contactName.trim()) next.contactName = '入力してください';
    if (!email.includes('@')) next.email = 'メールアドレスの形式で入力してください';
    // 具体的な強度規則はサーバ（assertPasswordPolicy）が唯一の正。
    // ここでは最低長だけ見て、往復を 1 回減らす。
    if (password.length < 12) next.password = '12 文字以上で設定してください';
    if (!consented) next.consent = '規約とプライバシーポリシーへの同意が必要です';
    return next;
  }

  /**
   * 入力欄の送信。**ここでは申し込まない。**
   *
   * 検証を通ったら最終確認へ進むだけ。実際の `POST /public/signup` は
   * 確認画面の「この内容で申し込む」でしか呼ばない。
   */
  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const next = validate();
    setErrors(next);
    if (hasErrors(next)) return;
    setStep('confirm');
  }

  /* ------------------------------------------------------ 受付できない場合 */

  const signupEnabled = configQuery.data?.features.signup ?? false;

  return (
    <div className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto w-full max-w-2xl space-y-6">
        <div className="space-y-2 text-center">
          <div className="brand-gradient mx-auto flex size-12 items-center justify-center rounded-xl text-white shadow-lg shadow-primary/25">
            <Mail className="size-6" aria-hidden />
          </div>
          <h1 className="text-xl font-semibold">{env.brandName} お申し込み</h1>
        </div>

        {configQuery.isPending ? <p className="text-center text-sm text-muted-foreground">読み込み中…</p> : null}

        {configQuery.data && !signupEnabled ? (
          <Alert variant="warning">
            <AlertTitle>現在、新規のお申し込みを受け付けていません</AlertTitle>
            <AlertDescription>
              掲示に必要な事業者情報が未設定のため、お申し込みを停止しています。
              <Link to="/legal" className="ml-1 underline">
                規約・法令表示
              </Link>
            </AlertDescription>
          </Alert>
        ) : null}

        {done ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="size-5 text-success" aria-hidden />
                お申し込みを受け付けました
              </CardTitle>
              <CardDescription>{done.message}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              <p>
                {email} 宛に確認メールを送信しました。メール内のリンクを開くまで、
                サービスの準備は開始されません（この時点では契約は成立していません）。
              </p>
              <ResendForm defaultEmail={email} />
            </CardContent>
          </Card>
        ) : signupEnabled && step === 'confirm' ? (
          <FinalConfirmation
            plan={selectedPlan}
            taxRateBp={taxRateBp}
            trialDays={trialDays}
            organizationName={organizationName.trim()}
            slug={slug.trim().toLowerCase()}
            email={email.trim()}
            domain={domain.trim().toLowerCase()}
            consentDocuments={consentDocuments}
            error={mutation.error}
            pending={mutation.isPending}
            onBack={() => setStep('form')}
            onConfirm={() => mutation.mutate()}
          />
        ) : signupEnabled ? (
          <Card>
            <CardHeader>
              <CardTitle>アカウントを作成する</CardTitle>
              <CardDescription>
                メールアドレスの確認が完了してから、サービスの準備を開始します。
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={onSubmit} noValidate>
                <FormAlert error={mutation.error} />

                <Field label="組織名" htmlFor="su-org" required error={errors.organizationName}>
                  <Input
                    id="su-org"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    autoComplete="organization"
                  />
                </Field>

                <Field
                  label="組織 ID"
                  htmlFor="su-slug"
                  required
                  error={errors.slug}
                  hint="英小文字・数字・ハイフン。後から変更できません。"
                >
                  <Input
                    id="su-slug"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    placeholder="example-inc"
                  />
                </Field>

                <Field label="ご担当者名" htmlFor="su-name" required error={errors.contactName}>
                  <Input
                    id="su-name"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    autoComplete="name"
                  />
                </Field>

                <Field
                  label="メールアドレス"
                  htmlFor="su-email"
                  required
                  error={errors.email}
                  hint="確認メールの宛先になります。使い捨てアドレスは受け付けません。"
                >
                  <Input
                    id="su-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                </Field>

                <Field
                  label="パスワード"
                  htmlFor="su-password"
                  required
                  error={errors.password}
                  hint="12 文字以上。メールアドレスや組織 ID と似た文字列は使えません。"
                >
                  <Input
                    id="su-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                </Field>

                <Field
                  label="利用予定の独自ドメイン（任意）"
                  htmlFor="su-domain"
                  hint="後から追加できます。"
                >
                  <Input
                    id="su-domain"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    placeholder="example.com"
                  />
                </Field>

                {/*
                  **税込（総額）で出す。** 特商法ページが「各プランの料金ページに
                  表示する金額（消費税込みの総額を表示します）」と掲示しており、
                  LP も税込を主に出しているのに、実際に申し込むこの画面だけが
                  税抜だった。金額はサーバが計算した `monthlyPriceInclTax` を
                  そのまま出し、画面では税額を計算しない。
                */}
                <Field
                  label="プラン"
                  htmlFor="su-plan"
                  required
                  hint={`表示は税込（消費税 ${taxRateBp / 100}%）の月額です。`}
                >
                  <select
                    id="su-plan"
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                    value={planCode}
                    onChange={(e) => setPlanCode(e.target.value)}
                  >
                    {plans.map((p) => (
                      <option key={p.code} value={p.code}>
                        {p.name}（月額 {formatCount(p.monthlyPriceInclTax)} 円・税込／税抜{' '}
                        {formatCount(p.monthlyPrice)} 円）
                      </option>
                    ))}
                  </select>
                </Field>

                {/*
                  **無料試用を申込画面に書く。** サーバは `trialDays` 日の
                  トライアルを作るのに、その事実がどの顧客向け画面にも
                  書かれていなかった。申込者は「今日から月額が掛かる」と
                  理解して申し込み、期間の終了時に予告なく有料契約へ
                  切り替わったことを事後通知で知る形になっていた。
                */}
                {trialDays > 0 ? (
                  <Alert variant="info">
                    <AlertTitle>{trialDays} 日間は無料でお試しいただけます</AlertTitle>
                    <AlertDescription>
                      お試し期間中の料金は発生しません。期間中に解約した場合も課金されません。
                      課金の開始日は次の確認画面に表示します。
                    </AlertDescription>
                  </Alert>
                ) : null}

                <div className="space-y-1">
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={consented}
                      onChange={(e) => setConsented(e.target.checked)}
                    />
                    <span>
                      {consentDocuments.map((d, i) => (
                        <React.Fragment key={d.slug}>
                          {i > 0 ? '・' : ''}
                          <Link to="/legal/$slug" params={{ slug: d.slug }} className="underline" target="_blank">
                            {d.title}
                          </Link>
                          <span className="text-xs text-muted-foreground">（版 {d.version}）</span>
                        </React.Fragment>
                      ))}
                      に同意します
                    </span>
                  </label>
                  {errors.consent ? <p className="text-xs text-destructive">{errors.consent}</p> : null}
                  {/*
                   * **禁止事項（AUP）への導線を申込画面に出す。**
                   *
                   * AUP は同意記録の対象ではない（`SIGNUP_CONSENT_KINDS` は
                   * `terms` と `privacy` の 2 件で、サーバーは他の種別を受け付けない）。
                   * 利用規約に取り込まれているので法的にはそれで足りるが、
                   * **何をすると契約を止められるのかを申込前に読めない**のは実務上まずい。
                   * 一括配信を前提にしたサービスで、規約本文の奥にしか
                   * 禁止事項が無いと「知らずに踏む」が普通に起きる。
                   * 別のチェックボックスにしないのは、
                   * 記録されない同意欄を作らない（押した事実が残らない）ため。
                   */}
                  <p className="pl-6 text-xs text-muted-foreground">
                    利用規約には{' '}
                    <Link to="/legal/$slug" params={{ slug: 'aup' }} className="underline" target="_blank">
                      禁止事項（AUP）
                    </Link>{' '}
                    が含まれます。ご同意いただく前に必ずお読みください。同意を得ていない宛先への
                    一斉配信など、禁止事項に該当する行為が確認された場合はご利用を停止することがあります。
                  </p>
                </div>

                <SubmitBar
                  submitLabel="内容を確認する"
                  pending={false}
                  hint="次の画面で、料金・支払時期・解約条件を確認してから申し込みます。"
                />
              </form>
            </CardContent>
          </Card>
        ) : null}

        <p className="text-center text-sm text-muted-foreground">
          すでにアカウントをお持ちですか？{' '}
          <Link to="/login" className="underline">
            ログイン
          </Link>
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ 最終確認画面 */

/**
 * 特商法 12 条の 6（特定申込みを受ける際の表示）の最終確認画面。
 *
 * **6 項目を送信ボタンと同じ画面に並べる**のがこの条文の要求:
 *   ① 分量（契約するプランの上限）
 *   ② 販売価格（**税込の総額**）
 *   ③ 支払時期および方法
 *   ④ 役務の提供時期（引渡時期）
 *   ⑤ 申込みの撤回・解除に関する事項
 *   ⑥ 申込期間の定めがあるときはその旨（本サービスには無いので出さない）
 *
 * **文言は法定ページと同じ値から出す。** 税込金額と税率は
 * `GET /public/plans` が計算した値、無料試用の日数は同じ応答の
 * `trialDays`（= サーバが `trialEndsAt` を作るときの `TRIAL_PERIOD_DAYS`）。
 * ここに数字を直書きすると、特商法ページ・LP・実装の 3 つ目のずれ元になる。
 */
function FinalConfirmation(props: {
  plan: PublicPlan | null;
  taxRateBp: number;
  trialDays: number;
  organizationName: string;
  slug: string;
  email: string;
  domain: string;
  consentDocuments: { slug: string; title: string; version: string }[];
  error: unknown;
  pending: boolean;
  onBack: () => void;
  onConfirm: () => void;
}) {
  const { plan, trialDays } = props;

  /**
   * 課金開始日。**「15 日目から」ではなく実日付を出す。**
   * サーバは申込時刻 + `trialDays` 日を `trialEndsAt` にし、その翌日から
   * 有料契約へ移行する。日付の丸めをここで作り込むと実装とずれるので、
   * 「お申込み日から N 日後」という数え方をそのまま日付にする。
   */
  const billingStartsAt = React.useMemo(() => {
    const d = new Date(Date.now() + trialDays * 24 * 3600 * 1000);
    return d.toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' });
  }, [trialDays]);

  const rows: { label: string; value: React.ReactNode }[] = [
    { label: 'お申し込み内容', value: `${props.organizationName}（組織 ID: ${props.slug}）` },
    { label: 'ご連絡先', value: props.email },
    ...(props.domain ? [{ label: '利用予定の独自ドメイン', value: props.domain }] : []),
    {
      label: '分量（プランの上限）',
      value: plan
        ? `独自ドメイン ${plan.limits.domainsMax || '無制限'} 件 ／ メールアドレス ${
            plan.limits.mailboxesMax || '無制限'
          } 件 ／ 合計容量 ${formatBytes(plan.limits.storageBytesTotal)} ／ 送信 ${formatCount(
            plan.limits.sendPerDayMax,
          )} 通/日`
        : '—',
    },
    {
      label: '販売価格',
      value: plan ? (
        <>
          <b>月額 {formatCount(plan.monthlyPriceInclTax)} 円（税込）</b>
          <span className="text-muted-foreground">
            {' '}
            ／ 税抜 {formatCount(plan.monthlyPrice)} 円・消費税 {props.taxRateBp / 100}%
          </span>
        </>
      ) : (
        '—'
      ),
    },
    ...(trialDays > 0
      ? [
          {
            label: '無料お試し期間',
            value: (
              <>
                {trialDays} 日間（この期間の料金は発生しません）。
                <b> {billingStartsAt} から有料契約へ移行し、課金を開始します。</b>
                期間中に解約された場合、料金は発生しません。
              </>
            ),
          },
        ]
      : []),
    {
      label: '支払時期・支払方法',
      value:
        '月額基本料は前払いです。クレジットカードは毎月の課金日に決済し、銀行振込は請求書の発行日から 30 日以内にお支払いいただきます。' +
        '上限を超えた分の従量料金は後払いで、同じ請求書に記載します。',
    },
    {
      label: '役務の提供時期',
      value:
        'ご登録のメールアドレス宛に確認メールをお送りします。メール内のリンクを開いた時点でサービスの準備を開始します（通常は即時）。',
    },
    {
      label: '申込みの撤回・解約',
      value:
        '解約はコントロールパネルからいつでもお手続きいただけます。月額プランの解約月の料金は、役務の提供を受けた日数に応じて日割りで按分計算します。' +
        '役務の性質上、提供開始後の返品はお受けできません。',
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>お申し込み内容のご確認</CardTitle>
        <CardDescription>
          この内容で申し込みます。金額・支払時期・解約条件をご確認ください。
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormAlert error={props.error} />

        <dl className="divide-y divide-border rounded-md border border-border text-sm">
          {rows.map((row) => (
            <div key={row.label} className="grid gap-1 p-3 sm:grid-cols-[11rem_1fr] sm:gap-4">
              <dt className="font-medium text-muted-foreground">{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>

        <p className="text-xs text-muted-foreground">
          {props.consentDocuments.map((d, i) => (
            <React.Fragment key={d.slug}>
              {i > 0 ? '・' : ''}
              <Link to="/legal/$slug" params={{ slug: d.slug }} className="underline" target="_blank">
                {d.title}
              </Link>
              （版 {d.version}）
            </React.Fragment>
          ))}
          {props.consentDocuments.length > 0 ? 'に同意のうえ申し込みます。' : null} 詳細な取引条件は{' '}
          <Link to="/legal/$slug" params={{ slug: 'tokushoho' }} className="underline" target="_blank">
            特定商取引法に基づく表記
          </Link>{' '}
          をご覧ください。同意した版と文面ハッシュ・接続元 IP を記録します。
        </p>

        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={props.onConfirm} loading={props.pending}>
            この内容で申し込む
          </Button>
          <Button type="button" variant="outline" onClick={props.onBack} disabled={props.pending}>
            入力に戻る
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------- 確認メール */

/** 確認メールの再送。**アドレスの存在は漏らさない**（サーバは常に 202）。 */
function ResendForm({ defaultEmail }: { defaultEmail: string }) {
  const [value, setValue] = React.useState(defaultEmail);
  const [sent, setSent] = React.useState(false);
  const mutation = useMutation({
    mutationFn: () => publicApi.post<{ ok: boolean }>('/signup/resend', { email: value.trim() }),
    onSuccess: () => setSent(true),
  });

  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
    >
      <Field label="確認メールが届かない場合" htmlFor="su-resend" className="min-w-56 flex-1">
        <Input id="su-resend" type="email" value={value} onChange={(e) => setValue(e.target.value)} />
      </Field>
      <Button type="submit" variant="outline" loading={mutation.isPending}>
        再送する
      </Button>
      {sent ? <p className="w-full text-xs text-muted-foreground">送信しました（数分あけて再度お試しください）。</p> : null}
    </form>
  );
}

/* ------------------------------------------------------------ 確認リンク */

/**
 * 確認メールのリンク先（`/signup/verify?token=...`）。
 *
 * **開いた瞬間に POST する。** GET で副作用を起こさないのが原則だが、
 * ここは「メールのリンクを踏む＝本人の意思表示」なので、画面側から
 * 明示的に POST を投げる形にしてある（リンク自体は GET のまま）。
 */
export function SignupVerifyPage() {
  const search = useSearchParams();
  const navigate = useAppNavigate();
  const token = typeof search.token === 'string' ? search.token : '';
  const [state, setState] = React.useState<'pending' | 'ok' | 'error'>('pending');
  const [message, setMessage] = React.useState('');
  const started = React.useRef(false);

  React.useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!token) {
      setState('error');
      setMessage('確認用のリンクが正しくありません。');
      return;
    }
    publicApi
      .post<{ ok: boolean }>('/signup/verify', { token })
      .then(() => setState('ok'))
      .catch((err: unknown) => {
        setState('error');
        setMessage(err instanceof ApiError ? errorMessage(err) : '確認に失敗しました。');
      });
  }, [token]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>メールアドレスの確認</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {state === 'pending' ? <p className="text-sm text-muted-foreground">確認しています…</p> : null}
          {state === 'ok' ? (
            <>
              <Alert variant="success">
                <AlertTitle>確認が完了しました</AlertTitle>
                <AlertDescription>
                  サービスの準備を開始しました。準備が整うとメールでお知らせします。
                </AlertDescription>
              </Alert>
              <Button onClick={() => navigate({ to: '/login' })}>ログインへ</Button>
            </>
          ) : null}
          {state === 'error' ? (
            <>
              <Alert variant="destructive">
                <AlertTitle>確認できませんでした</AlertTitle>
                <AlertDescription>{message}</AlertDescription>
              </Alert>
              <ResendForm defaultEmail="" />
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
